import { useState, useEffect } from 'react';
import { getPromoSlides, createPromoSlide, updatePromoSlide, deletePromoSlide, uploadPromoMedia, API_HOST } from '../services/api';

const ICONS = ['home','celebration','card_giftcard','verified','account_balance_wallet','location_on','trending_up','star','local_offer','money_off','lock_open','account_balance','engineering'];
const TARGETS = ['both','customer','worker'];
const BASE = API_HOST;

export default function Promos() {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title:'', subtitle:'', description:'', icon:'home', color1:'#6C63FF', color2:'#4F46E5', target:'both', sort_order:0, is_active:true });

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try { setSlides(await getPromoSlides()); } catch(_) {}
    setLoading(false);
  };

  const resetForm = () => { setForm({ title:'', subtitle:'', description:'', icon:'home', color1:'#6C63FF', color2:'#4F46E5', target:'both', sort_order:0, is_active:true }); setEditing(null); };

  const handleSave = async () => {
    if (!form.title || !form.subtitle) { alert('Title and subtitle required'); return; }
    try {
      if (editing) { await updatePromoSlide(editing, form); }
      else { await createPromoSlide(form); }
      resetForm(); load();
    } catch(e) { alert('Failed: ' + (e.response?.data?.detail || 'Error')); }
  };

  const handleEdit = (s) => {
    setEditing(s.id);
    setForm({ title: s.title, subtitle: s.subtitle, description: s.description, icon: s.icon, color1: s.color1, color2: s.color2, target: s.target, sort_order: s.sort_order, is_active: s.is_active });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this slide?')) return;
    try { await deletePromoSlide(id); load(); } catch(_) { alert('Failed'); }
  };

  const handleToggle = async (s) => {
    try { await updatePromoSlide(s.id, { is_active: !s.is_active }); load(); } catch(_) {}
  };

  const handleUpload = async (id, file) => {
    try { await uploadPromoMedia(id, file); load(); alert('Media uploaded!'); }
    catch(e) { alert('Upload failed: ' + (e.response?.data?.detail || 'Error')); }
  };

  return (
    <div>
      <div className="page-header">
        <h1>🎠 Promo Slides</h1>
        <p>Manage carousel slides for customer and worker apps</p>
      </div>

      {/* Form */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header"><h2>{editing ? '✏️ Edit Slide' : '➕ Add New Slide'}</h2></div>
        <div style={{ padding: 16, display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
          <input placeholder="Title (e.g. Launch Offer 🎉)" value={form.title} onChange={e=>setForm({...form, title:e.target.value})} style={inputStyle} />
          <input placeholder="Subtitle" value={form.subtitle} onChange={e=>setForm({...form, subtitle:e.target.value})} style={inputStyle} />
          <input placeholder="Description" value={form.description} onChange={e=>setForm({...form, description:e.target.value})} style={inputStyle} />
          <select value={form.icon} onChange={e=>setForm({...form, icon:e.target.value})} style={inputStyle}>
            {ICONS.map(i=><option key={i} value={i}>{i}</option>)}
          </select>
          <div style={{display:'flex', gap:8, alignItems:'center'}}>
            <label style={{fontSize:12}}>Color 1:</label>
            <input type="color" value={form.color1} onChange={e=>setForm({...form, color1:e.target.value})} />
            <label style={{fontSize:12}}>Color 2:</label>
            <input type="color" value={form.color2} onChange={e=>setForm({...form, color2:e.target.value})} />
          </div>
          <select value={form.target} onChange={e=>setForm({...form, target:e.target.value})} style={inputStyle}>
            {TARGETS.map(t=><option key={t} value={t}>{t}</option>)}
          </select>
          <input type="number" placeholder="Sort Order" value={form.sort_order} onChange={e=>setForm({...form, sort_order:parseInt(e.target.value)||0})} style={inputStyle} />
          <div style={{display:'flex', gap:12, alignItems:'center'}}>
            <button className="btn btn-sm btn-success" onClick={handleSave}>{editing ? 'Update' : 'Add Slide'}</button>
            {editing && <button className="btn btn-sm btn-danger" onClick={resetForm}>Cancel</button>}
          </div>
        </div>
      </div>

      {/* List */}
      <div className="card">
        <div className="card-header">
          <h2>📋 All Slides ({slides.length})</h2>
          <button className="refresh-btn" onClick={load}>🔄 Refresh</button>
        </div>
        {loading ? <div className="loading">Loading...</div> : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Preview</th>
                  <th>Title</th>
                  <th>Subtitle</th>
                  <th>Target</th>
                  <th>Order</th>
                  <th>Active</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {slides.map(s => (
                  <tr key={s.id}>
                    <td>
                      <div style={{width:120, height:50, borderRadius:8, background:`linear-gradient(135deg, ${s.color1}, ${s.color2})`, display:'flex', alignItems:'center', justifyContent:'center'}}>
                        <span style={{color:'#fff', fontSize:10, fontWeight:'bold', textAlign:'center', padding:4}}>{s.title}</span>
                      </div>
                    </td>
                    <td><strong>{s.title}</strong><br/><span style={{fontSize:11, color:'#999'}}>{s.description}</span></td>
                    <td>{s.subtitle}</td>
                    <td><span className={`badge ${s.target==='customer'?'badge-info':s.target==='worker'?'badge-warning':'badge-success'}`}>{s.target}</span></td>
                    <td>{s.sort_order}</td>
                    <td><button className={`btn btn-sm ${s.is_active?'btn-success':'btn-danger'}`} onClick={()=>handleToggle(s)}>{s.is_active?'ON':'OFF'}</button></td>
                    <td>
                      <div style={{display:'flex', gap:4, alignItems:'center'}}>
                        <button className="btn btn-sm btn-warning" onClick={()=>handleEdit(s)}>✏️</button>
                        <label className="btn btn-sm" style={{background:'#1E88E5',color:'#fff',cursor:'pointer',margin:0}}>
                          📎
                          <input type="file" accept=".jpg,.jpeg,.png,.gif,.webp,.mp4,.mov,.avi,.webm" style={{display:'none'}}
                            onChange={e => { if(e.target.files[0]) handleUpload(s.id, e.target.files[0]); e.target.value=''; }} />
                        </label>
                        <button className="btn btn-sm btn-danger" onClick={()=>handleDelete(s.id)}>🗑️</button>
                      </div>
                      {s.media_url && (
                        <div style={{marginTop:4}}>
                          {s.media_type === 'video' ? (
                            <video src={`${BASE}${s.media_url}`} style={{width:80, borderRadius:4}} muted />
                          ) : (
                            <img src={`${BASE}${s.media_url}`} alt="" style={{width:80, borderRadius:4}} />
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const inputStyle = { padding:'8px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:13 };
